// Chemin: C:\gestion-copro\src\components\sentinelle\AvertissementSentinelleModal.tsx
// Description: Modal d'avertissement SENTINELLE pour messages à risque moyen
// Permet d'envoyer quand même ou de modifier
// Session 52 : création initiale - Spec SENTINELLE V2

import React, { useState } from 'react';
import {
  Shield,
  AlertCircle,
  Send,
  Pencil,
  X,
  ChevronDown,
  ChevronUp,
  Users,
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface AlerteSCUD {
  type: string;
  message: string;
  extrait?: string;
  suggestion?: string;
}

interface AvertissementSentinelleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Alertes détectées par l'analyse SCUD */
  alertes: AlerteSCUD[];
  /** Nature de la communication */
  nature: 'courante' | 'engageante';
  /** Callback pour envoyer malgré l'avertissement (si courante) */
  onEnvoyerQuandMeme?: () => void;
  /** Callback pour soumettre au vote CS (si engageante) */
  onSoumettreVoteCS?: () => void;
  /** Callback quand l'utilisateur veut modifier */
  onModifier: () => void;
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const AvertissementSentinelleModal: React.FC<AvertissementSentinelleModalProps> = ({
  isOpen,
  onClose,
  alertes,
  nature,
  onEnvoyerQuandMeme,
  onSoumettreVoteCS,
  onModifier,
}) => {
  const [showAlertes, setShowAlertes] = useState(true);
  const [confirmEnvoi, setConfirmEnvoi] = useState(false);

  if (!isOpen) return null;

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      terme_risque: 'Termes à risque',
      accusation: 'Accusations',
      imperatif: 'Formulations impératives',
      menace: 'Menaces ou ultimatums',
      emotion: 'Contenu émotionnel',
      juridique: 'Risques juridiques',
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string): string => {
    const icons: Record<string, string> = {
      terme_risque: '⚠️',
      accusation: '⛔',
      imperatif: '📢',
      menace: '🚨',
      emotion: '😤',
      juridique: '⚖️',
    };
    return icons[type] || '⚠️';
  };

  const handleEnvoyerQuandMeme = () => {
    if (!confirmEnvoi) {
      setConfirmEnvoi(true);
      return;
    }
    onEnvoyerQuandMeme?.();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl">
          {/* Header - Orange pour risque moyen */}
          <div className="bg-orange-500 text-white px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Avertissement SENTINELLE</h2>
                  <p className="text-orange-100 text-sm">
                    Risque moyen détecté - Vérification recommandée
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Message d'explication */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-orange-800 font-medium">
                    Ce message pourrait être amélioré
                  </p>
                  <p className="text-orange-700 text-sm mt-1">
                    SENTINELLE a détecté {alertes.length} point{alertes.length > 1 ? 's' : ''} d'attention. 
                    {nature === 'courante' 
                      ? ' Vous pouvez envoyer malgré tout ou modifier le message.'
                      : ' Cette communication engageante nécessite le vote du Conseil Syndical.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Badge nature */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-gray-500">Nature de la communication :</span>
              {nature === 'courante' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Courante (opérationnelle)
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Engageante (vote CS requis)
                </span>
              )}
            </div>

            {/* Section alertes (dépliable) */}
            <div className="mb-6">
              <button
                onClick={() => setShowAlertes(!showAlertes)}
                className="flex items-center gap-2 text-gray-700 font-medium hover:text-gray-900 transition-colors"
              >
                {showAlertes ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                Points d'attention ({alertes.length})
              </button>

              {showAlertes && (
                <div className="mt-3 space-y-2">
                  {alertes.map((alerte, index) => (
                    <div 
                      key={index} 
                      className="bg-white border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{getTypeIcon(alerte.type)}</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">
                            {getTypeLabel(alerte.type)}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">{alerte.message}</p>
                          {alerte.extrait && (
                            <div className="mt-2 px-3 py-2 bg-orange-50 rounded border-l-4 border-orange-300">
                              <p className="text-gray-600 text-sm italic">"{alerte.extrait}"</p>
                            </div>
                          )}
                          {alerte.suggestion && (
                            <p className="text-green-700 text-sm mt-2">
                              💡 {alerte.suggestion}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Message de confirmation (si envoi direct demandé) */}
            {confirmEnvoi && nature === 'courante' && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4">
                <p className="text-amber-800 font-medium">
                  Êtes-vous sûr de vouloir envoyer ce message sans modification ?
                </p>
                <p className="text-amber-700 text-sm mt-1">
                  Vous prenez la responsabilité d'envoyer un message avec des points d'attention non corrigés.
                </p>
              </div>
            )}
          </div>

          {/* Footer avec actions */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onModifier}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Modifier le message
              </button>

              {nature === 'courante' && onEnvoyerQuandMeme && (
                <button
                  onClick={handleEnvoyerQuandMeme}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    confirmEnvoi
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  }`}
                >
                  <Send className="w-4 h-4" />
                  {confirmEnvoi ? 'Confirmer l\'envoi' : 'Envoyer quand même'}
                </button>
              )}

              {nature === 'engageante' && onSoumettreVoteCS && (
                <button
                  onClick={onSoumettreVoteCS}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  <Users className="w-4 h-4" />
                  Soumettre au vote du CS
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvertissementSentinelleModal;
